import { EditorState } from '@codemirror/state';
import { SyntaxNode } from '@lezer/common';
import {
  Identifier,
  LabelMatcher,
  LabelMatchers,
  LabelName,
  QuotedLabelName,
  StringLiteral,
  VectorSelector,
} from '@/prometheus/lezer/parser.terms';
import { walkBackward } from './path-finder';

export function getMetricName(tree: SyntaxNode, state: EditorState): string {
  let metricName = getMetricNameInVectorSelector(tree, state);
  if (metricName === '') {
    metricName = getMetricNameFromLabelMatchers(tree, state);
  }
  return metricName;
}

export function getMetricNameInVectorSelector(tree: SyntaxNode, state: EditorState): string {
  // Find if there is a defined metric name. Should be used to autocomplete a labelValue or a labelName
  // First find the parent "VectorSelector" to be able to find then the subChild "Identifier" if it exists.
  let currentNode: SyntaxNode | null = walkBackward(tree, VectorSelector);
  if (!currentNode) {
    // Weird case that shouldn't happen, because "VectorSelector" is by definition the parent of the LabelMatchers.
    return '';
  }
  currentNode = currentNode.getChild(Identifier);
  if (!currentNode) {
    return '';
  }
  return state.sliceDoc(currentNode.from, currentNode.to);
}

export function getMetricNameFromLabelMatchers(tree: SyntaxNode, state: EditorState): string {
  let current: SyntaxNode | null = walkBackward(tree, LabelMatchers);
  if (!current) {
    return '';
  }

  current = current.firstChild;

  while (current !== null) {
    const child = current.firstChild;

    switch (current.type.id) {
      case LabelMatcher:
        let stringValue = '';

        switch (child?.type.id) {
          case LabelName:
            stringValue = state.sliceDoc(child.from, child.to);
            break;
          case QuotedLabelName:
            stringValue = state.sliceDoc(child.from, child.to).slice(1, -1);
            break;
        }

        if (stringValue === '__name__') {
          // AST at this point: LabelMatcher(LabelName,MatchOp(operator),StringLiteral)
          const matcherValueNode = current.getChild(StringLiteral);
          if (matcherValueNode) {
            return state.sliceDoc(matcherValueNode.from, matcherValueNode.to).slice(1, -1);
          }
        }
        break;
      case QuotedLabelName:
        // Any non-empty quoted string will be longer than 2 characters:
        if (current.to - current.from > 2) {
          return state.sliceDoc(current.from, current.to).slice(1, -1);
        }
        break;
    }
    current = current.nextSibling;
  }

  return '';
}
